import axios from "axios";
import { Client, Patient, Plan, Layout, Visit, Job, AssignmentInfo,
    PatientVisit, SchedulableSlot, PatientTrackables, PatientRoster, 
    DocumentRef} from "../models/core";
import { InsertOneResult, ListResponse } from "./SharedAPI";
import { Insurance } from "../models/insurance";
import { analytics } from "./analytics/zipAnalytics";
import { DBHistory } from "../models/history";

type NoId<T> = Omit<T, 'id'|'_id'>;
type PartialWithRequired<T, K extends keyof T> = Partial<T> & Pick<T, K>;

/*interface ReturnPackage<T> {
    readonly id:number;
    readonly success:boolean;
    readonly error?:string;
    readonly data?:T|undefined;
}*/

class _API {
    readonly baseUrl: string;
    token: string;
    user: string;
    requestId:number;
    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
        this.token = '';
        this.requestId = 0;
        this.user = '';
    }

    getClients = async (
        filter: { [k in keyof Client]?: string | { $in: string[] } } = {}, 
        sort: [string, number][] = []
      ) => {
        const queryString = Object.entries(filter)
          .map(([key, value]) => {
            if (typeof value === 'object' && '$in' in value) {
              return `${key}=${value.$in.map(encodeURIComponent).join(',')}`;
            } else {
              return `${key}=${encodeURIComponent(value as string)}`;
            }
          })
          .concat(`sort=${encodeURIComponent(JSON.stringify(sort))}`)
          .join('&');

        return await this.get<ListResponse<Client>>(`clients?${queryString}`);
    };

    /*getClientAggregates = async (clientId:string) => await this.post<
        ReturnPackage<ReturnPackage<{ client?: Client | undefined; plans?: Plan[] | undefined;
            filters?: { assign?: string[] | undefined; } | undefined; }>>, unknown
    >(`clients/${clientId}/aggregates/client`);*/

    getClient = async (clientId: string) => await this.get<Client>('clients/' + clientId);
    getClientsByPractice = async (practiceId: string) => await this.get<ListResponse<Client>>(`clients?practiceId=${practiceId}`);
    createClient = async (client: NoId<Client>) => await this.post('clients', client);
    updateClient = async (client: Client) => await this.put('clients/' + client._id, client);
    deleteClient = async (clientId: string) => await this.delete('clients/' + clientId);

    getPlans = async (clientId: string) => await this.get<ListResponse<Plan>>(`clients/${clientId}/plans`);
    getPlan = async (clientId: string, planId: string) => await this.get<Plan>(`clients/${clientId}/plans/${planId}`);
    createPlan = async (clientId: string, plan:NoId<Plan>) => await this.post<InsertOneResult, NoId<Plan>>(`clients/${clientId}/plans`, plan);

    getPatients = async (clientId: string, query: [string, string][], limit = 10, offset = 0, sort: [string, number][] = []) =>
        await this.get<ListResponse<PatientVisit>>(`clients/${clientId}/patients?limit=${limit}&offset=${offset}&sort=${JSON.stringify(sort)}&${query.map(([k, v]) => k + '=' + encodeURIComponent(v)).join('&')}`);

    getPatient = async (clientId: string, patientId: string) =>
        await this.get<Patient>(`clients/${clientId}/patients/${patientId}`);
    
    getPatientByZCMID = async (clientId: string, ziphyId: string) =>
        await this.get<Patient>(`clients/${clientId}/patients/zcmId?zcmId=${ziphyId}`);

    getPatientsQuery = async (query: [string, string][]) =>
        await this.get<ListResponse<Patient>>(`clients/0/patients?limit=10&offset=0&${query.map(([k, v]) => k + '=' + encodeURIComponent(v)).join('&')}`);

    createPatient = async (patient: NoId<Patient>) =>
        await this.post<InsertOneResult,NoId<Patient>>(`clients/${patient.clientId}/patients`, patient);

    updatePatient = async (patient: PartialWithRequired<Patient, '_id'>) =>
        await this.put<Patient, PartialWithRequired<Patient, '_id'>>(`clients/${patient.clientId}/patients/${patient._id}`, patient);

    deletePatient = async (clientId: string, patientId: string) =>
        await this.delete<Patient>(`clients/${clientId}/patients/${patientId}`);

    assignPatients = async (clientId: string, assignmentInfo: AssignmentInfo, query: [string, string][]) =>
        await this.put<AssignmentInfo>(`clients/${clientId}/assigns/0?${query.map(([k,v]) => k+'='+encodeURIComponent(v)).join('&')}`, assignmentInfo);

    getVisits = async (clientId: string, patientId?: string, query?:[string,string][]) => 
        await this.get<ListResponse<Visit>>(`clients/${clientId}/patients/${patientId}/visits?limit=10000&${(query||[]).map(([k,v]) => k+'='+encodeURIComponent(v)).join('&')}`);

    getVisit = async (clientId: string, patientId: string, visitId: string) =>
        await this.get<Visit>(`clients/${clientId}/patients/${patientId}/visits/${visitId}`);

    createVisit = async (visit: NoId<Visit>) =>
        await this.post<InsertOneResult,NoId<Visit>>(`clients/${visit.clientId}/patients/${visit.patientId}/visits`, visit);

    updateVisit = async (visit: Partial<Visit>) =>
        await this.put<Visit, Partial<Visit>>(`clients/${visit.clientId}/patients/${visit.patientId}/visits/${visit._id}`, visit);

    getPatientTrackables = async (clientId: string, patientId: string) =>
        await this.get<ListResponse<PatientTrackables>>(`clients/${clientId}/patients/${patientId}/trackables?limit=10000`);

    createPatientTrackables = async (trackable: NoId<PatientTrackables>) =>
        await this.post(`clients/${trackable.clientId}/patients/${trackable.patientId}/trackables`, trackable);

    updatePatientTrackables = async (trackable: PatientTrackables) =>
        await this.put<PatientTrackables>(`clients/${trackable.clientId}/patients/${trackable.patientId}/trackables/${trackable._id}`, trackable);

    getRosters = async (clientId: string, patientId: string) =>
        await this.get<ListResponse<PatientRoster>>(`clients/${clientId}/rosters?patientId=${patientId}`);
    
    createRosters = async (clientId: string, roster:NoId<PatientRoster>) =>
        await this.post<unknown, NoId<PatientRoster>>(`clients/${clientId}/rosters`, roster);

    //import = async (clientId: string) => await this.post(`clients/${clientId}/import`, { });

    previewSheet = async(layout: Layout) =>
        (await axios.post<{ name: string; columns: string[] }[]>(
            this.baseUrl+'sheets/preview', 
            JSON.stringify(layout), 
            {headers: {'Content-Type': 'application/json'}})
        ).data;

    createSheet = async(layout: Layout, email: string) =>
        (await axios.post<{sheetId: string}>(
            this.baseUrl+'sheets/create', 
            JSON.stringify({email, layout}), 
            {headers: {'Content-Type': 'application/json'}})
        ).data;

    testImportScript = async(code: string, sheetId: string) => 
        (await axios.post<string>(
            this.baseUrl+'scripts/test_import',
            { code, sheetId }
        )).data

    testExportScript = async(code: string, clientId: string) => 
        (await axios.post<string>(
            this.baseUrl+'scripts/test_export',
            { code, clientId }
        )).data

    export = async(code: string, sheetId: string, clientId: string) => 
        (await axios.post<string>(
            this.baseUrl+'scripts/export',
            { code, clientId, sheetId }
        )).data

    getJobs = async (clientId: string, type = '', sort: [string, number][] = []) =>
        await this.get<ListResponse<Job>>(`clients/${clientId}/jobs`
            + `?type=${type}`
            + (sort.length>0 ? `&sort=${JSON.stringify(sort)}` : '')
        )

    getJobsMeta = async (clientId: string, type = '', sort: [string, number][] = []) =>
        await this.get<ListResponse<Job>>(`clients/${clientId}/jobs/meta`
            + `?type=${type}`
            + (sort.length>0 ? `&sort=${JSON.stringify(sort)}` : '')
        )

    getJob = async (clientId: string, jobId: string) =>
        await this.get<Job>(`clients/${clientId}/jobs/${jobId}`)

    createJob = async <T extends Job>(job: NoId<T>) =>
        await this.post<InsertOneResult, NoId<T>>(
            `clients/${job.clientId}/jobs`,
            job
        )
    
    updateJob = async (job: Job) => await this.put<Job>(`clients/${job.clientId}/jobs/${job._id}`, job);

    updateJobResult = async (job: Job, filter: {[k:string]: string}, data: unknown) =>
        await this.put<Job, unknown>(
            `clients/${job.clientId}/jobs/${job._id}/result?`+
            Object.entries(filter).map(([k, v]) => k + '=' + encodeURIComponent(v)).join('&'), 
            data
        );

    deleteJob = async (clientId: string, jobId: string) => await this.delete<Job>(`clients/${clientId}/jobs/${jobId}`);

    runJob = async (clientId: string, job: Job|string) => 
        await this.post(`clients/${clientId}/jobs/${typeof(job)=='string' ? job : job._id}/run`, {})

    terminateJob = async (clientId: string, job: Job) => await this.post(`clients/${clientId}/jobs/${job._id}/terminate`,{})
        
    getPatientValues = async(clientId: string, field: string) =>
        await this.get<string[]>(`clients/${clientId}/patients/values?field=${field}`);

    getSchedulableSlot = async(clientId: string, patientId: string) => 
        await this.get<SchedulableSlot>(`clients/${clientId}/schedulables/patient?patientId=${patientId}`);

    updateSchedulableSlots = async(clientId: string, slots:SchedulableSlot[]) => 
        await this.put<SchedulableSlot[]>(`clients/${clientId}/schedulables/0`, slots);

    saveSchedulableSlots = async(clientId: string, jobId:string, startTime:string, user:string, slotSize ='15') => 
        await this.put<SchedulableSlot[]>(`clients/${clientId}/schedulables/0?jobId=${encodeURIComponent(jobId)}`
            +`&startTime=${encodeURIComponent(startTime)}&user=${encodeURIComponent(user)}`
            +`&slotSize=${encodeURIComponent(slotSize)}`, []);

    getInsurance = async (clientId: string, patientId: string, insuranceId: string) =>
        await this.get<Insurance>(`clients/${clientId}/patients/${patientId}/insurances/${insuranceId}`);

    getInsuranceList = async (clientId: string, patientId: string) =>
        await this.get<ListResponse<Insurance>>(`clients/${clientId}/patients/${patientId}/insurances`);

    createInsurance = async (clientId: string, patientId: string, insurance: NoId<Insurance>) =>
        await this.post<InsertOneResult, NoId<Insurance>>(`clients/${clientId}/patients/${patientId}/insurances`, insurance);
    
    //createInsurances = async (clientId: string, patientId: string, insuranceList: NoId<Insurance>[]) =>
    //    await this.post<InsertOneResult, NoId<Insurance>[]>(`clients/${clientId}/patients/${patientId}/insurances/multiple`, insuranceList);

    updateInsurance = async (clientId: string, patientId: string, insurance:PartialWithRequired<Insurance,'_id'>) =>
        await this.put<Insurance>(`clients/${clientId}/patients/${patientId}/insurances/${insurance._id}`, insurance);

    deleteInsurance = async (clientId: string, patientId: string, insuranceId: string) =>
        await this.delete<Insurance>(`clients/${clientId}/patients/${patientId}/insurances/${insuranceId}`);

    
    createDocument = async (client:Client, patientId:string, check:string, document:NoId<DocumentRef>) => {
        return await this.post<{acknowledged:boolean, insertedId:string, data:{ url:string}},
            {
                patientId:string,
                practiceId:string,
                check:string,
                document:NoId<DocumentRef>
            }>
            (`clients/${client._id.toString()}/patients/${patientId}/documents`,
            {
                patientId,
                practiceId:client.practiceId.toString(),
                check,
                document}
            );
    }

    createDocumentLink = async (document:DocumentRef) => 
        await this.post<{acknowledged:boolean, insertedId:string, data:{ url:string}},
            {patientId:string, document:DocumentRef}>
                (`clients/${document.clientId.toString()}/patients/${document.patientId.toString()}/documents/downloadLink`,
                {patientId:document.patientId.toString(), document}
            );

    abortDocument = async (clientId: string, patientId: string, documentId:string) => 
        await this.post<InsertOneResult, {patientId:string, documentId:string}>
            (`clients/${clientId}/patients/${patientId}/documents/abort`, {patientId,documentId});
    
    finalizeDocument = async (clientId: string, patientId: string, documentId:string) => 
        await this.post<InsertOneResult, {patientId:string, documentId:string}>
            (`clients/${clientId}/patients/${patientId}/documents/finalize`, {patientId,documentId});

    getDocumentsList = async (clientId: string, patientId: string) => 
        await this.get<ListResponse<DocumentRef>>(`clients/${clientId}/patients/${patientId}/documents`);

    getHistoryList = async (clientId: string, entry?:string, patientId?:string, 
        type?:'patient'|'client'|'insurance'|'visit'|'measure', limit = 100, offset = 0) => {
        return await this.get<ListResponse<DBHistory>>(`clients/${clientId}/historys?limit=${limit}&offset=${offset}`
            + `${entry?'&entry='+entry:''}${patientId?'&patientId='+patientId:''}${type?'&type='+type:''}`);
    }

    async get<T>(path: string, url?:string): Promise<T> {
        this.requestId += 1;
        try {
            const response = (await axios.get<T>(
                (url || this.baseUrl) + path,
                { headers: { Authorization: this.token } }
            ))
            return response?.data;
        } catch(error) {
            console.error('Error thrown: ', error);

            if(axios.isAxiosError(error)) {
                const errorString = 'error: ' + error.response?.status + ' details: ' + error.response?.data;
                console.error(errorString);
                analytics.error('api_error', { error:errorString }, true);
            }
            throw error;
        }
    }

    async post<T, U = T>(path: string, data?: U, url?:string): Promise<T> {
        this.requestId += 1;
        return (await axios.post<T>(
            (url || this.baseUrl) + path,
            JSON.stringify({id:this.requestId, user:this.user, data:data}),
            {
                headers: {
                    'Authorization': this.token,
                    'Content-Type': 'application/json'
                }
            }
        )).data;
    }

    async put<T, U = T>(path: string, data: U): Promise<T> {
        this.requestId += 1;
        return (await axios.put<T>(
            this.baseUrl + path,
            JSON.stringify({id:this.requestId, user:this.user, data:data}),
            {
                headers:
                {
                    'Authorization': this.token,
                    'Content-Type': 'application/json'
                }
            }
        )).data;
    }

    async delete<T>(path: string): Promise<void> {
        this.requestId += 1;
        await axios.delete<T>(
            this.baseUrl + path,
            { headers: { Authorization: this.token } }
        );
    }
}

export const API = new _API(process.env.REACT_APP_API_ENDPOINT || 'http://localhost:5005/');